/**
 * TALA — MembraneLite, tala cycles and typed konnakol.
 *
 * Chirp provenance: Chirp/flameback.html (MEMBRANE with its seeded noise and
 * chronological damping, the TALAS table, KONNAKOL_MAP/parseKonnakol and
 * layKonnakol's gati auto-fit).
 *
 * TYPED KONNAKOL IS THE CORE INPUT. Voice recognition is explicitly NOT
 * implemented: there is no microphone path, no speech model and no plan
 * pretending otherwise in this build. You type the syllables; the parser lays
 * them on the grid and tells you which tokens it did not recognise.
 */

import { createTalaEngine, type TalaEngine } from '../audio/tala/membrane-lite';
import type { Task } from '../audio/transport';
import { TalaAdapter } from '../mapping/adapters';
import { getExperiments } from '../mapping/config';
import type { ControlState, FeatureFrame, TalaIntent } from '../mapping/types';
import {
  emptyGrid, fitPhrase, KONNAKOL_EXAMPLES, layPhrase, parseKonnakol,
  STROKE_ORDER, TALAS, TALA_IDS, type Grid, type StrokeId, type TalaId,
} from '../music/talas';
import { button, chips, el, panel, slider, toggle } from '../ui/controls';
import type { Scene, SceneContext } from './types';

export function createTalaScene(): Scene {
  let engine: TalaEngine | null = null;
  let ctxRef: SceneContext | null = null;
  let task: Task | null = null;
  let tala: TalaId = 'adi';
  let spb = 2;
  let bpm = 84;
  let steps = TALAS[tala].beats * spb;
  let grid: Grid = emptyGrid(steps);
  let step = 0;
  const adapter = new TalaAdapter();
  let last: TalaIntent | null = null;
  let parseNote = 'type a phrase — it lays as you type';
  /** While running, free strikes are written into the grid at the current step. */
  let recStrikes = false;
  let layDebounce = 0;

  let gridEl: HTMLElement;
  let noteEl: HTMLElement;
  let field: HTMLTextAreaElement;

  function drawGrid(): void {
    // The row the pitch hand is aiming at is lit before any strike — the grid
    // half of "know what I'd get". Cells are BUTTONS: tapping toggles the
    // stroke at that step, which makes the sequencer directly editable instead
    // of reachable only through typed konnakol.
    const aim = last?.stroke;
    gridEl.replaceChildren(...STROKE_ORDER.map((id: StrokeId) =>
      el('div', { class: `grow${id === aim ? ' hov' : ''}` }, [
        el('span', { class: 'gname' }, [id]),
        ...grid[id].map((on, i) => {
          const cell = el('button', {
            type: 'button', role: 'switch', 'aria-checked': String(on),
            'aria-label': `${id} step ${i + 1}`,
            class: `gcell${on ? ' on' : ''}${i === step ? ' cur' : ''}${i % spb === 0 ? ' beat' : ''}`,
          });
          cell.addEventListener('click', () => {
            grid[id][i] = !grid[id][i];
            drawGrid();
          });
          return cell;
        }),
      ])));
  }

  function resize(): void {
    steps = Math.min(32, TALAS[tala].beats * spb);
    grid = emptyGrid(steps);
    step = 0;
    drawGrid();
  }

  function lay(): void {
    const parsed = parseKonnakol(field.value);
    const fit = fitPhrase(parsed.steps.length, tala, spb);
    if (fit.fitted && fit.spb !== spb) { spb = fit.spb; steps = fit.gridSteps; }
    const res = layPhrase(parsed.steps, steps);
    grid = res.grid;
    drawGrid();
    const bits = [`${parsed.steps.length} syllables → ${res.laid} strokes on ${steps} steps (gati ${spb})`];
    if (!fit.fitted) bits.push(`did not divide ${TALAS[tala].beats} beats cleanly — kept gati ${spb}`);
    if (fit.overflow) bits.push(`${fit.overflow} past one avartanam, dropped`);
    if (parsed.unknown.length) bits.push(`unrecognised: ${parsed.unknown.join(', ')} (laid as rests)`);
    parseNote = bits.join(' · ');
    noteEl.textContent = parseNote;
  }

  function start(): void {
    if (!ctxRef || task) return;
    task = ctxRef.transport.addTask({
      bpm, division: spb,
      cb: (t, i) => {
        step = i % steps;
        const vel = last?.velocity ?? 0.9;
        for (const id of STROKE_ORDER) if (grid[id][step]) engine?.strike(id, t, vel);
        drawGrid();
      },
    });
    adapter.setRunning(true);
  }
  function stop(): void { task?.cancel(); task = null; adapter.setRunning(false); }

  return {
    id: 'tala',
    label: 'Tala',
    blurb: 'A two-headed drum on a tala cycle. Raise or lower the pitch hand to choose the stroke, pinch to strike. Type konnakol to lay a phrase — there is no voice input in this build.',
    padMode: 'xy',
    // All eight strokes on the height axis, not the default 7 scale degrees.
    selectionSteps: STROKE_ORDER.length,

    mount(host, ctx) {
      ctxRef = ctx;
      engine = createTalaEngine(ctx.bus.ctx, ctx.bus.input);
      gridEl = el('div', { class: 'talagrid', 'aria-label': 'Stroke grid' });
      noteEl = el('p', { class: 'note' }, [parseNote]);
      field = el('textarea', {
        class: 'konnakol', id: 'konnakol', rows: '2', spellcheck: 'false',
        placeholder: 'tha ka dhi mi',
        'aria-label': 'Konnakol phrase — space-separated syllables, punctuation is a rest',
      }) as HTMLTextAreaElement;
      field.value = 'tha ka dhi mi ki ta tha ka';
      // Lay as you type (debounced) — the Lay button stays as an explicit
      // re-lay, but the common path should not require finding it.
      field.addEventListener('input', () => {
        window.clearTimeout(layDebounce);
        layDebounce = window.setTimeout(lay, 300);
      });
      resize();

      host.append(panel('Tala', TALAS[tala].tip, [
        chips<TalaId>({
          label: 'Tala', value: tala,
          options: TALA_IDS.map((id) => ({ id, label: TALAS[id].label, title: TALAS[id].tip })),
          onChange: (id) => { tala = id; resize(); ctx.setStatus(TALAS[id].tip); },
        }),
        chips<string>({
          label: 'Gati', value: String(spb),
          options: [2, 3, 4].map((g) => ({ id: String(g), label: `${g}/beat` })),
          onChange: (g) => { spb = Number(g); resize(); task?.setDivision(spb); },
        }),
        el('label', { class: 'field', for: 'konnakol' }, [
          el('span', { class: 'slabel' }, ['Konnakol — typed']),
          field,
        ]),
        el('div', { class: 'row wrap' }, [
          ...KONNAKOL_EXAMPLES.map((ex) => button(ex.label, () => { field.value = ex.text; lay(); }, 'ghost small')),
        ]),
        el('div', { class: 'row' }, [
          button('Lay phrase', lay),
          button('Clear', () => { grid = emptyGrid(steps); drawGrid(); }, 'ghost'),
          toggle('Run', false, (on) => { on ? start() : stop(); }, 'Play the avartanam'),
          toggle('Rec strikes', false, (on) => { recStrikes = on; },
            'While running, your free strikes are written into the grid at the current step'),
        ]),
        noteEl,
        gridEl,
        slider({ label: 'Tempo', min: 40, max: 160, step: 1, value: bpm, format: (v) => `${v} bpm`, onInput: (v) => { bpm = v; task?.setBpm(v); } }),
        el('p', { class: 'note' }, ['Voice recognition is not implemented in this build. Typed input is the whole interface, on purpose.']),
      ]));
      ctx.setStatus(TALAS[tala].tip);
    },

    update(frame: FeatureFrame, control: ControlState) {
      const prevStroke = last?.stroke;
      const intent = adapter.update(frame, control);
      last = intent;
      // Keep the aim highlight live while idle — while running, the transport
      // callback already redraws every step.
      if (intent.stroke !== prevStroke && !task) drawGrid();
      engine?.setLevel(intent.level);
      engine?.setSruti(2, intent.cents);
      // Gate 4: the free strike lands on the tala's own akshara grid when
      // quantized, sampling velocity at fire-decision time.
      if (intent.strike && engine && ctxRef) {
        // The stroke the pitch hand selected — no longer a hardcoded 'tha'.
        const stroke = intent.stroke;
        if (getExperiments().trigger === 'quantized') {
          ctxRef.transport.at(
            ctxRef.transport.nextGrid(bpm, 2),
            (t) => engine?.strike(stroke, t, last?.velocity ?? intent.velocity),
          );
        } else {
          engine.strike(stroke, ctxRef.bus.ctx.currentTime + 0.02, intent.velocity);
        }
        // Live record: keep the phrase you just played. Written at the current
        // step — the grid redraws on the next transport tick.
        if (recStrikes && task) grid[stroke][step] = true;
      }
    },

    readouts() {
      return [
        ['stroke', last ? last.stroke : '—'],
        ['tala', TALAS[tala].label],
        ['step', `${step + 1}/${steps}`],
        ['sruti', last ? `${last.cents >= 0 ? '+' : ''}${last.cents}¢` : '—'],
        ['level', last ? last.level.toFixed(2) : '—'],
      ];
    },

    selection() {
      if (!last) return null;
      const active = STROKE_ORDER.indexOf(last.stroke);
      return { count: STROKE_ORDER.length, active, label: last.stroke };
    },

    unmount() { stop(); engine?.dispose(); engine = null; },
  };
}
